/**
 * ═══════════════════════════════════════════════════════════════
 *  SIDEBAR MODE — Per-role rollback flag for Sidebar Fold Plan
 * ═══════════════════════════════════════════════════════════════
 *
 * Default: every role is on "new" (folded) sidebar.
 * Override: a role can be reverted to "legacy" without route changes.
 * User override: a single user can flip their own sidebar via
 * localStorage (matches UI Mode Framework pattern).
 *
 * Routes are NEVER affected by this flag — only sidebar visibility.
 */

import { useEffect, useState } from "react";
import type { UserRole } from "./RoleContext";

export type SidebarMode = "new" | "legacy";

/**
 * Per-role default. Flip a value to "legacy" to revert that role
 * fleet-wide without touching routes or the fold registry.
 */
export const ROLE_SIDEBAR_MODE: Record<UserRole, SidebarMode> = {
  super_admin: "new",
  owner: "new",
  ops_manager: "new",
  admin: "new",
  warehouse_manager: "new",
  installer: "new",
  subcontractor: "new",
  sales: "new",
  sales_manager: "new",
  call_center: "new",
  cfo: "new",
  finance_manager: "new",
  bookkeeper: "new",
  head_of_installation: "new",
  procurement_manager: "new",
  inventory_manager: "new",
  it_staff: "new",
};

const USER_OVERRIDE_KEY = "koredor.sidebarMode.override";

/**
 * Resolve the active sidebar mode for a role:
 *   1. user-level localStorage override (if set)
 *   2. role-level default
 */
export function getSidebarMode(role: UserRole): SidebarMode {
  if (typeof window !== "undefined") {
    const override = window.localStorage.getItem(USER_OVERRIDE_KEY);
    if (override === "new" || override === "legacy") return override;
  }
  return ROLE_SIDEBAR_MODE[role];
}

/**
 * Hook: returns current mode + setter. Setter persists to localStorage.
 * Pass `null` to clear the user override and fall back to role default.
 */
export function useSidebarMode(role: UserRole): {
  mode: SidebarMode;
  setUserOverride: (mode: SidebarMode | null) => void;
} {
  const [mode, setMode] = useState<SidebarMode>(() => getSidebarMode(role));

  useEffect(() => {
    setMode(getSidebarMode(role));
  }, [role]);

  const setUserOverride = (next: SidebarMode | null) => {
    if (typeof window === "undefined") return;
    if (next === null) {
      window.localStorage.removeItem(USER_OVERRIDE_KEY);
    } else {
      window.localStorage.setItem(USER_OVERRIDE_KEY, next);
    }
    setMode(getSidebarMode(role));
  };

  return { mode, setUserOverride };
}
